import { analytics } from 'firebaseApp';
import { observeAuthState, type User } from '../firebase/authClient';
import {
  observeUserCards,
  observeUserProfile,
  saveUserProfile,
} from '../firebase/userFirestore';
import { observeVenueUsersForUser } from '../firebase/venueFirestore';
import { accountCleared } from './actions';
import {
  venuesFailed,
  venuesLoading,
  venuesReceived,
} from './slices/admin.slice';
import {
  authFailed,
  authResolved,
  cardsFailed,
  cardsReceived,
  profileFailed,
  profileReceived,
  type SessionProfile,
  type SessionUser,
} from './slices/session.slice';
import type { AppDispatch } from './store';

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

const serializeUser = (user: User): SessionUser => ({
  uid: user.uid,
  displayName: user.displayName,
  email: user.email,
  emailVerified: user.emailVerified,
  phoneNumber: user.phoneNumber,
  photoURL: user.photoURL,
});

const serializeProfile = (data: Partial<Pagcomanda.User>): SessionProfile => {
  const { createdAt, ...profile } = data;
  const timestamp = createdAt as unknown as { toDate?: () => Date };
  const date = createdAt instanceof Date ? createdAt : timestamp?.toDate?.();

  return {
    ...profile,
    createdAt: date?.toISOString() ?? null,
  };
};

export const subscribeToSession = () => (dispatch: AppDispatch) => {
  let active = true;
  let generation = 0;
  let currentUserId: string | null | undefined;
  let userUnsubscribers: Array<() => void> = [];

  const stopUserSubscriptions = () => {
    generation += 1;
    userUnsubscribers.forEach((unsubscribe) => unsubscribe());
    userUnsubscribers = [];
  };

  const authUnsubscribe = observeAuthState((user) => {
    if (!active) return;
    stopUserSubscriptions();

    if (!user) {
      currentUserId = null;
      analytics.setUserId('');
      dispatch(accountCleared());
      return;
    }

    if (currentUserId && currentUserId !== user.uid) {
      dispatch(accountCleared());
    }
    currentUserId = user.uid;

    const currentGeneration = generation;
    const isCurrent = () => active && currentGeneration === generation;

    analytics.setUserId(user.uid);
    dispatch(authResolved(serializeUser(user)));
    dispatch(venuesLoading());

    let creatingProfile = false;
    const profileUnsubscribe = observeUserProfile(
      user.uid,
      (snapshot) => {
        if (!isCurrent()) return;

        if (snapshot.exists()) {
          dispatch(profileReceived(serializeProfile(snapshot.data())));
        } else if (!creatingProfile) {
          creatingProfile = true;
          saveUserProfile({
            name: user.displayName,
            email: user.email,
            isEmailVerified: user.emailVerified,
            mobile: user.phoneNumber,
            avatarUrl: user.photoURL,
            createdAt: new Date(),
          }, user.uid).catch((error) => {
            if (isCurrent()) dispatch(profileFailed(errorMessage(error)));
          });
        }
      },
      (error) => {
        if (isCurrent()) dispatch(profileFailed(errorMessage(error)));
      },
    );

    userUnsubscribers.push(profileUnsubscribe);

    const cardsUnsubscribe = observeUserCards(
      user.uid,
      (snapshot) => {
        if (!isCurrent()) return;
        dispatch(cardsReceived(snapshot.docs.map((card) => ({
          id: card.id,
          data: card.data(),
        }))));
      },
      (error) => {
        if (isCurrent()) dispatch(cardsFailed(errorMessage(error)));
      },
    );

    userUnsubscribers.push(cardsUnsubscribe);

    const venuesUnsubscribe = observeVenueUsersForUser(
      user.uid,
      (snapshot) => {
        if (!isCurrent()) return;
        dispatch(venuesReceived(snapshot.docs.map((venue) => {
          const data = venue.data();
          return {
            id: venue.id,
            venueId: data.venueId,
            userId: data.userId,
            role: data.role ?? [],
          };
        })));
      },
      (error) => {
        if (isCurrent()) dispatch(venuesFailed(errorMessage(error)));
      },
    );

    userUnsubscribers.push(venuesUnsubscribe);
  }, (error) => {
    if (active) dispatch(authFailed(errorMessage(error)));
  });

  return () => {
    if (!active) return;
    active = false;
    stopUserSubscriptions();
    authUnsubscribe();
  };
};
